// Shared helpers for the JSON column kind. Used by the inline JsonCell so
// preview, prettifying, and validation rules stay consistent in one place.

import { ICellValue, IJsonCellValue } from 'types'

export type ParseResult = { ok: true; value: IJsonCellValue | null } | { ok: false; error: string }

export const previewOf = (value: IJsonCellValue | null): string => {
  if (value === null || value === undefined) return ''
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

export const prettyOf = (value: IJsonCellValue | null): string => {
  if (value === null || value === undefined) return ''
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return ''
  }
}

export const validateJsonObject = (text: string): ParseResult => {
  const trimmed = text.trim()
  if (trimmed === '') return { ok: true, value: null }
  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (e) {
    return {
      ok: false,
      error: `This isn't valid JSON (${(e as Error).message}). Check for missing quotes, commas or brackets.`
    }
  }
  if (parsed === null) return { ok: true, value: null }
  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'A JSON cell must hold a single object wrapped in { }, like {"status": "open"}.' }
  }
  return { ok: true, value: parsed as IJsonCellValue }
}

// --- Ordering and grouping -------------------------------------------------
//
// Rows are re-sorted in the browser (sortRows, called from the data context with the view's
// sort settings), so a JSON column has to be ordered here the same way Postgres orders the
// jsonb column server-side — otherwise the grid shows a different order from the API.
// Postgres compares jsonb by type first, then structurally:
//
//   null < string < number < boolean < array < object
//
// Objects compare by number of pairs before their pairs, arrays by length before their
// elements, and numbers compare numerically rather than as text. Object pairs are walked in
// jsonb's storage order (key length, then bytewise) while the keys themselves compare as
// ordinary strings. String comparison uses the
// database collation, which a browser can't reproduce exactly; code-unit order is the closest
// available and agrees with it for the ASCII keys and values these columns hold in practice.

// Two different orderings are at play, and conflating them gets the comparison wrong.
//
// STORAGE order — jsonb stores an object's keys sorted by length first, then bytewise, so
// {"aa":1,"b":2} is held as {"b": 2, "aa": 1}. This is the order the pairs are *walked* in when
// two objects are compared, and the order jsonb renders them in.
const compareStorageKeys = (a: string, b: string): number => {
  if (a.length !== b.length) return a.length - b.length
  return a < b ? -1 : a > b ? 1 : 0
}

const jsonbKeyOrder = (value: Record<string, unknown>): string[] => Object.keys(value).sort(compareStorageKeys)

const jsonTypeRank = (value: unknown): number => {
  if (value === null || value === undefined) return 0
  if (typeof value === 'string') return 1
  if (typeof value === 'number') return 2
  if (typeof value === 'boolean') return 3
  if (Array.isArray(value)) return 4
  return 5
}

const compareScalars = <T>(a: T, b: T): number => (a < b ? -1 : a > b ? 1 : 0)

/** Compare two parsed JSON values the way Postgres compares the equivalent jsonb. */
export const compareJsonValues = (a: unknown, b: unknown): number => {
  const rank = jsonTypeRank(a)
  const otherRank = jsonTypeRank(b)
  if (rank !== otherRank) return compareScalars(rank, otherRank)

  if (rank === 0) return 0
  if (rank === 1) return compareScalars(a as string, b as string)
  if (rank === 2) return compareScalars(a as number, b as number)
  if (rank === 3) return compareScalars(Number(a as boolean), Number(b as boolean))

  if (rank === 4) {
    const left = a as unknown[]
    const right = b as unknown[]
    if (left.length !== right.length) return compareScalars(left.length, right.length)
    for (let index = 0; index < left.length; index++) {
      const result = compareJsonValues(left[index], right[index])
      if (result !== 0) return result
    }
    return 0
  }

  const left = a as Record<string, unknown>
  const right = b as Record<string, unknown>
  const leftKeys = jsonbKeyOrder(left)
  const rightKeys = jsonbKeyOrder(right)
  if (leftKeys.length !== rightKeys.length) return compareScalars(leftKeys.length, rightKeys.length)
  for (let index = 0; index < leftKeys.length; index++) {
    // Pairs are walked in storage order above, but the keys themselves compare as ordinary
    // strings — {"aa":1} sorts before {"b":1} even though "b" is the shorter key.
    const keyResult = compareScalars(leftKeys[index], rightKeys[index])
    if (keyResult !== 0) return keyResult
    const valueResult = compareJsonValues(left[leftKeys[index]], right[rightKeys[index]])
    if (valueResult !== 0) return valueResult
  }
  return 0
}

/**
 * Compare two JSON *cells*, treating an empty cell the way compareSortValues treats a blank —
 * last ascending, which the direction multiplier flips to first descending. That matches
 * Postgres, whose default is NULLS LAST for ASC and NULLS FIRST for DESC.
 */
export const compareJsonCells = (a: ICellValue, b: ICellValue): number => {
  const aEmpty = a === null || a === undefined
  const bEmpty = b === null || b === undefined
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  return compareJsonValues(a, b)
}

/**
 * A stable grouping key for a JSON cell.
 *
 * Serialised with keys in jsonb's own order rather than via plain JSON.stringify, which would
 * preserve whatever order the user typed: a cell edited in this session holds the locally
 * parsed object (the JSON editor commits JSON.parse output straight to state), so it has not
 * been through Postgres' normalisation yet. Without this, {"b":2,"a":1} and {"a":1,"b":2} would
 * group apart until the page was reloaded. Built as a string rather than by rebuilding the
 * object because JS reorders integer-like keys ahead of the rest whatever order they're set in.
 */
const canonicalJson = (value: unknown): string => {
  if (value === null || value === undefined) return 'null'
  if (typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  const object = value as Record<string, unknown>
  return `{${jsonbKeyOrder(object)
    .map((key) => `${JSON.stringify(key)}:${canonicalJson(object[key])}`)
    .join(',')}}`
}

export const canonicalJsonKey = (value: ICellValue): string => {
  if (value === null || value === undefined) return ''
  return canonicalJson(value)
}
